import type { BreedingOptions, BreedingResult, Dragon, MultiTargetCombo, ParentCombo, PathPlan, PathStep } from "../types";
import { formatPercent } from "./format";

const DEFAULT_OPTIONS: BreedingOptions = {
  userLevel: 99,
  allowSameSpecies: false,
  activeRateUpDids: new Set<number>()
};

function elementKey(ids: number[]) {
  return [...ids].sort((a, b) => a - b).join("-");
}

function combinations<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [[]];
  if (items.length < size) return [];
  const result: T[][] = [];
  const walk = (start: number, acc: T[]) => {
    if (acc.length === size) {
      result.push(acc);
      return;
    }
    for (let index = start; index < items.length; index += 1) {
      walk(index + 1, [...acc, items[index]]);
    }
  };
  walk(0, []);
  return result;
}

function parentElementComboKeys(parentA: Dragon, parentB: Dragon) {
  const pool = [...new Set([...parentA.elementIds, ...parentB.elementIds])].sort((a, b) => a - b);
  const keys = new Set<string>();
  for (let size = 1; size <= Math.min(3, pool.length); size += 1) {
    combinations(pool, size).forEach((combo) => keys.add(elementKey(combo)));
  }
  return keys;
}

type ParentContext = {
  elementCount: number;
  comboKeys: Set<string>;
  dids: Set<number>;
};

function getParentContext(parentA: Dragon, parentB: Dragon): ParentContext {
  const parentPool = new Set([...parentA.elementIds, ...parentB.elementIds]);
  return {
    elementCount: parentPool.size,
    comboKeys: parentElementComboKeys(parentA, parentB),
    dids: new Set([parentA.id, parentB.id])
  };
}

function getCandidateFailure(child: Dragon, parentA: Dragon, parentB: Dragon, context: ParentContext, options: BreedingOptions) {
  if (child.requireUserLevel > options.userLevel) return "테이머 레벨";

  if (child.breeding.requireParentElementRule && !context.comboKeys.has(elementKey(child.sortedElementIds))) {
    return "속성 조합";
  }

  if (child.elementIds.length >= 3 && (parentA.elementIds.length < 2 || parentB.elementIds.length < 2)) {
    return "3속성 부모";
  }

  if (context.elementCount < child.breeding.parentElementCount) return "부모 속성 수";

  for (const requiredElement of child.breeding.parentElementRequirementIds) {
    if (!context.comboKeys.has(String(requiredElement))) return "필수 속성";
  }

  for (const did of child.breeding.parentDidRequirements) {
    if (!context.dids.has(did)) return "필수 부모";
  }

  return null;
}

function canUseParent(dragon: Dragon, options: BreedingOptions) {
  return dragon.canBreedAsParent && dragon.requireUserLevelByElement <= options.userLevel;
}

export function getParentWarnings(parentA: Dragon, parentB: Dragon, options: Partial<BreedingOptions> = {}) {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  const warnings: string[] = [];
  if (!canUseParent(parentA, merged)) warnings.push(`${parentA.name}: 부모 교배 조건 미충족`);
  if (!canUseParent(parentB, merged)) warnings.push(`${parentB.name}: 부모 교배 조건 미충족`);
  if (!merged.allowSameSpecies && parentA.id === parentB.id) {
    warnings.push("게임 UI 기준 동일 품종 부모는 교배할 수 없습니다.");
  }
  return warnings;
}

export function calculateBreedingResults(
  dragons: Dragon[],
  parentA: Dragon,
  parentB: Dragon,
  tierFixedRateMax: Record<string, number>,
  options: Partial<BreedingOptions> = {}
) {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  if (!merged.allowSameSpecies && parentA.id === parentB.id) return [];

  if (parentA.elementIds.length === 1 && parentB.elementIds.length === 1 && parentA.id === parentB.id) {
    return [
      makeResult({
        dragon: parentA,
        chance: 100,
        mode: "fixed",
        rawWeight: 0,
        fixedBasisPoints: 10000,
        reasonTags: ["동일 품종 강제 계산"]
      })
    ];
  }

  const fixedByTier = new Map<number, Map<number, number>>();
  const weightedCandidates: Dragon[] = [];
  const context = getParentContext(parentA, parentB);

  for (const child of dragons) {
    if (getCandidateFailure(child, parentA, parentB, context, merged) !== null) continue;

    const fixed = child.breeding.fixedProportion;
    if (fixed > 0) {
      const tierMap = fixedByTier.get(child.tier) ?? new Map<number, number>();
      tierMap.set(child.id, fixed);
      fixedByTier.set(child.tier, tierMap);
      continue;
    }

    weightedCandidates.push(child);
  }

  const fixedResults: BreedingResult[] = [];
  for (const [tier, tierMap] of fixedByTier.entries()) {
    const maxRate = tierFixedRateMax[String(tier)];
    const fixedSum = [...tierMap.values()].reduce((sum, value) => sum + value, 0);
    const scale = maxRate !== undefined && fixedSum > maxRate && fixedSum > 0 ? maxRate / fixedSum : 1;

    for (const [did, basisPoints] of tierMap.entries()) {
      const dragon = dragons.find((item) => item.id === did);
      if (!dragon) continue;

      const baseAdjusted = basisPoints * scale;
      let adjusted = baseAdjusted;
      if (merged.activeRateUpDids.has(did)) {
        adjusted += basisPoints * 0.5;
      }

      fixedResults.push(
        makeResult({
          dragon,
          chance: adjusted / 100,
          baseChance: baseAdjusted / 100,
          isRateUp: merged.activeRateUpDids.has(did),
          mode: "fixed",
          rawWeight: 0,
          fixedBasisPoints: adjusted,
          reasonTags: buildReasonTags(dragon, merged)
        })
      );
    }
  }

  const weightedItems = weightedCandidates
    .map((dragon) => {
      const base = dragon.breeding.portion;
      const rateUpBonus = merged.activeRateUpDids.has(dragon.id) ? base * 1.5 : 0;
      return { dragon, baseWeight: base, weight: base + rateUpBonus };
    })
    .filter((item) => item.weight > 0);

  const fixedChance = fixedResults.reduce((sum, item) => sum + item.chance, 0);
  const baseFixedChance = fixedResults.reduce((sum, item) => sum + item.baseChance, 0);
  const residualChance = Math.max(0, 100 - fixedChance);
  const baseResidualChance = Math.max(0, 100 - baseFixedChance);
  const weightSum = weightedItems.reduce((sum, item) => sum + item.weight, 0);
  const baseWeightSum = weightedItems.reduce((sum, item) => sum + item.baseWeight, 0);
  const weightedResults = weightedItems.map(({ dragon, baseWeight, weight }) =>
    makeResult({
      dragon,
      chance: weightSum > 0 ? (weight / weightSum) * residualChance : 0,
      baseChance: baseWeightSum > 0 ? (baseWeight / baseWeightSum) * baseResidualChance : 0,
      isRateUp: merged.activeRateUpDids.has(dragon.id),
      mode: "weighted",
      rawWeight: weight,
      fixedBasisPoints: 0,
      reasonTags: buildReasonTags(dragon, merged)
    })
  );

  return [...fixedResults, ...weightedResults].sort((a, b) => b.chance - a.chance || a.dragon.id - b.dragon.id);
}

export function findParentCombos(
  dragons: Dragon[],
  targetDid: number,
  tierFixedRateMax: Record<string, number>,
  options: Partial<BreedingOptions> = {}
) {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  const parentPool = dragons.filter((dragon) => canUseParent(dragon, merged));
  const combos: ParentCombo[] = [];

  for (let a = 0; a < parentPool.length; a += 1) {
    for (let b = a + 1; b < parentPool.length; b += 1) {
      const parentA = parentPool[a];
      const parentB = parentPool[b];
      const results = calculateBreedingResults(dragons, parentA, parentB, tierFixedRateMax, merged);
      const target = results.find((result) => result.dragon.id === targetDid);
      if (!target || target.chance <= 0) continue;

      combos.push({
        parentA,
        parentB,
        target,
        resultCount: results.length,
        expectedAttempts: 100 / target.chance,
        // 실제 부화 시간(= 알 타이머). brd_sec == hatching_sec 라 한 값만 사용. (이전: ×기대횟수 = 오해 소지)
        expectedSeconds: target.hatchingSeconds,
        byproducts: results.filter((result) => result.dragon.id !== targetDid).slice(0, 5)
      });
    }
  }

  return combos.sort((a, b) => b.target.chance - a.target.chance || a.expectedSeconds - b.expectedSeconds);
}

export function findMultiTargetCombos(
  dragons: Dragon[],
  targetDids: number[],
  tierFixedRateMax: Record<string, number>,
  options: Partial<BreedingOptions> = {}
) {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  const dragonById = new Map(dragons.map((dragon) => [dragon.id, dragon]));
  const selectedTargets = [...new Set(targetDids)]
    .map((did) => dragonById.get(did))
    .filter((dragon): dragon is Dragon => Boolean(dragon));

  if (selectedTargets.length === 0) return [];

  const parentPool = dragons.filter((dragon) => canUseParent(dragon, merged));
  const combos: MultiTargetCombo[] = [];

  for (let a = 0; a < parentPool.length; a += 1) {
    for (let b = a + 1; b < parentPool.length; b += 1) {
      const parentA = parentPool[a];
      const parentB = parentPool[b];
      const results = calculateBreedingResults(dragons, parentA, parentB, tierFixedRateMax, merged);
      const resultByDid = new Map(results.map((result) => [result.dragon.id, result]));
      const targets = selectedTargets
        .map((dragon) => resultByDid.get(dragon.id))
        .filter((result): result is BreedingResult => Boolean(result && result.chance > 0));

      if (targets.length !== selectedTargets.length) continue;

      const totalChance = targets.reduce((sum, target) => sum + target.chance, 0);
      const baseTotalChance = targets.reduce((sum, target) => sum + target.baseChance, 0);
      const expectedSeconds = Math.min(...targets.map((target) => target.hatchingSeconds));

      combos.push({
        parentA,
        parentB,
        targets,
        missedTargets: [],
        hitCount: targets.length,
        totalTargets: selectedTargets.length,
        totalChance,
        totalChanceText: formatPercent(totalChance),
        baseTotalChance,
        baseTotalChanceText: formatPercent(baseTotalChance),
        bestChance: Math.max(...targets.map((target) => target.chance)),
        resultCount: results.length,
        expectedAttempts: 100 / totalChance,
        expectedSeconds
      });
    }
  }

  return combos.sort(
    (a, b) =>
      b.hitCount - a.hitCount ||
      b.totalChance - a.totalChance ||
      b.bestChance - a.bestChance ||
      a.expectedSeconds - b.expectedSeconds
  );
}

export function findShortestPath(
  dragons: Dragon[],
  ownedDids: Set<number>,
  targetDid: number,
  tierFixedRateMax: Record<string, number>,
  options: Partial<BreedingOptions> = {}
): PathPlan {
  const target = dragons.find((dragon) => dragon.id === targetDid);
  if (!target) {
    throw new Error(`Unknown target did: ${targetDid}`);
  }
  if (ownedDids.has(targetDid)) return { target, steps: [], reachable: true };

  const merged = { ...DEFAULT_OPTIONS, ...options };
  const dragonById = new Map(dragons.map((dragon) => [dragon.id, dragon]));
  const known = new Set(ownedDids);
  const parent = new Map<number, { from: [number, number]; chance: number }>();
  const maxDepth = 6;

  for (let depth = 0; depth < maxDepth; depth += 1) {
    const knownBefore = known.size;
    const available = [...known]
      .map((did) => dragonById.get(did))
      .filter((dragon): dragon is Dragon => {
        if (!dragon) return false;
        return canUseParent(dragon, merged);
      });

    for (let a = 0; a < available.length; a += 1) {
      const parentA = available[a];
      if (!parentA) continue;
      for (let b = a + 1; b < available.length; b += 1) {
        const parentB = available[b];
        if (!parentB) continue;
        const results = calculateBreedingResults(dragons, parentA, parentB, tierFixedRateMax, merged);
        for (const result of results) {
          if (result.chance <= 0 || known.has(result.dragon.id)) continue;
          known.add(result.dragon.id);
          parent.set(result.dragon.id, {
            from: [parentA.id, parentB.id],
            chance: result.chance
          });
          if (result.dragon.id === targetDid) {
            return buildPathPlan(target, targetDid, parent, dragonById);
          }
        }
      }
    }

    if (known.size === knownBefore) break;
  }

  return { target, steps: [], reachable: false };
}

function buildPathPlan(
  target: Dragon,
  targetDid: number,
  parent: Map<number, { from: [number, number]; chance: number }>,
  dragonById: Map<number, Dragon>
) {
  const steps: PathStep[] = [];
  const visited = new Set<number>();
  const collect = (did: number) => {
    if (visited.has(did)) return;
    const info = parent.get(did);
    if (!info) return;
    collect(info.from[0]);
    collect(info.from[1]);
    const parentA = dragonById.get(info.from[0]);
    const parentB = dragonById.get(info.from[1]);
    const child = dragonById.get(did);
    if (!parentA || !parentB || !child) return;
    steps.push({ from: [parentA, parentB] as [Dragon, Dragon], to: child, chance: info.chance });
    visited.add(did);
  };
  collect(targetDid);
  return { target, steps, reachable: true };
}

function makeResult(args: {
  dragon: Dragon;
  chance: number;
  baseChance?: number;
  isRateUp?: boolean;
  mode: "fixed" | "weighted";
  rawWeight: number;
  fixedBasisPoints: number;
  reasonTags: string[];
}): BreedingResult {
  const baseChance = args.baseChance ?? args.chance;
  return {
    dragon: args.dragon,
    chance: args.chance,
    chanceText: formatPercent(args.chance),
    baseChance,
    baseChanceText: formatPercent(baseChance),
    isRateUp: args.isRateUp ?? false,
    mode: args.mode,
    rawWeight: args.rawWeight,
    fixedBasisPoints: args.fixedBasisPoints,
    breedingSeconds: args.dragon.breedingSeconds,
    hatchingSeconds: args.dragon.hatchingSeconds,
    reasonTags: args.reasonTags
  };
}

function buildReasonTags(dragon: Dragon, options: BreedingOptions) {
  const tags: string[] = [];
  if (dragon.breeding.fixedProportion > 0) tags.push("고정 확률");
  if (options.activeRateUpDids.has(dragon.id)) tags.push("확률업");
  if (dragon.breeding.parentElementCount > 0) tags.push("부모 속성 조건");
  if (dragon.drop !== "breed_normal") tags.push(dragon.drop);
  return tags;
}
